/**
 * HeroSection - Landing page hero with headline, subheadline and CTAs
 *
 * Shows different CTAs based on auth state:
 * - Not authenticated: "Try it free" + "Log in"
 * - Authenticated: "Go to app"
 *
 * Features:
 * - Shimmer text animation on headline
 * - Breathing logo animation
 */

"use client";

import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { motion, useReducedMotion } from "framer-motion";
import GradientButton from "@/components/ui/GradientButton";
import Logo from "@/components/icons/Logo";
import { ShimmerText, ScrollReveal } from "./effects";
import { isRegistrationEnabled } from "@/lib/feature-flags";
import { useAuth } from "@/context/AuthContext";
import { trackCtaClicked } from "@/lib/analytics";

export default function HeroSection() {
  const t = useTranslations("landing.hero");
  const tCommon = useTranslations("common");
  const shouldReduceMotion = useReducedMotion();
  const { isAuthenticated: isAuthenticatedFn } = useAuth();
  const isAuthenticated = isAuthenticatedFn();

  return (
    <section className="relative py-16 md:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Logo + Name */}
        <ScrollReveal delay={0}>
          <div className="flex items-center justify-center space-x-3 mb-8">
            <motion.div
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              animate={
                !shouldReduceMotion
                  ? {
                      scale: [1, 0.98, 1],
                      opacity: [1, 0.85, 1],
                    }
                  : {}
              }
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Logo className="w-10 h-10" />
            </motion.div>
            <span className="text-white text-3xl font-bold tracking-tight">
              {tCommon("appName")}
            </span>
          </div>
        </ScrollReveal>

        {/* Headline */}
        <ScrollReveal delay={0.1}>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 max-w-4xl mx-auto">
            <ShimmerText>{t("headline")}</ShimmerText>
          </h1>
        </ScrollReveal>

        {/* Subheadline */}
        <ScrollReveal delay={0.2}>
          <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            {t("subheadline")}
          </p>
        </ScrollReveal>

        {/* CTAs */}
        <ScrollReveal delay={0.3}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {isAuthenticated ? (
              <Link
                href="/build-preset"
                onClick={() => trackCtaClicked({ location: "hero", label: "go_to_app" })}
              >
                <GradientButton size="lg" glow>
                  {t("goToApp")}
                </GradientButton>
              </Link>
            ) : (
              <>
                <Link
                  href="/register"
                  onClick={() =>
                    trackCtaClicked({
                      location: "hero",
                      label: isRegistrationEnabled ? "try_free" : "join_waitlist",
                    })
                  }
                >
                  <GradientButton size="lg" glow>
                    {isRegistrationEnabled ? t("tryFree") : t("joinWaitlist")}
                  </GradientButton>
                </Link>
                <Link
                  href="/login"
                  onClick={() => trackCtaClicked({ location: "hero", label: "log_in" })}
                >
                  <button className="h-14 px-8 text-lg font-semibold text-gray-300 border border-gray-600 rounded-lg hover:border-gray-500 hover:text-white transition-all duration-300">
                    {t("login")}
                  </button>
                </Link>
              </>
            )}
          </div>
        </ScrollReveal>

        {/* Timing message */}
        <ScrollReveal delay={0.4}>
          <p className="mt-6 text-sm text-gray-400">
            {t("timingMessage")}
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
}
